import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useOfficerTitles } from "@/hooks/useOfficerTitles";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Download,
  Upload,
  Trash2,
  ArrowLeft,
  File,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  FileArchive,
  FileAudio,
  Search,
  User,
  Calendar,
  HardDrive,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

const getFileIcon = (filename) => {
  const ext = filename?.split(".").pop()?.toLowerCase() || "";
  if (["pdf"].includes(ext)) return <FileText className="w-8 h-8 text-red-400" />;
  if (["doc", "docx", "txt", "rtf"].includes(ext)) return <FileText className="w-8 h-8 text-blue-400" />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <FileSpreadsheet className="w-8 h-8 text-green-400" />;
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext))
    return <FileImage className="w-8 h-8 text-purple-400" />;
  if (["mp4", "mov", "avi", "mkv", "webm", "m4v"].includes(ext))
    return <FileVideo className="w-8 h-8 text-pink-400" />;
  if (["mp3", "wav", "ogg", "flac", "m4a", "aac"].includes(ext))
    return <FileAudio className="w-8 h-8 text-yellow-400" />;
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext))
    return <FileArchive className="w-8 h-8 text-orange-400" />;
  return <File className="w-8 h-8 text-slate-400" />;
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "Unknown";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

/**
 * Admin-only storage indicator for the Downloads page.
 *
 * Shows total declared bytes vs. durable MongoDB bytes side by side. The
 * gap between the two is the signal that older uploads lost their chunks
 * before MongoDB-backed persistence landed — those rows need to be
 * re-uploaded to survive future redeploys.
 */
const StorageUsageBar = ({ usage }) => {
  if (!usage) return null;
  const declared = Number(usage.declared_bytes || 0);
  const mongo = Number(usage.mongo_bytes || 0);
  const filesCount = Number(usage.files_count || 0);
  const orphanBytes = Math.max(0, declared - mongo);
  const hasOrphans = orphanBytes > 1024;
  return (
    <div
      className="mb-4 rounded-lg border border-slate-700 bg-slate-900/70 p-3 text-xs sm:text-sm"
      data-testid="downloads-storage-usage"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="flex items-center gap-1.5">
          <HardDrive className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-slate-400">Durable (Mongo):</span>
          <span className="font-semibold text-cyan-300" data-testid="downloads-storage-mongo">
            {formatBytes(mongo)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-400">Declared:</span>
          <span className="font-semibold text-slate-200">{formatBytes(declared)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-400">Files:</span>
          <span className="font-semibold text-slate-200">{filesCount}</span>
        </div>
        {Array.isArray(usage.by_extension) && usage.by_extension.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">Top:</span>
            <span className="font-mono text-slate-300">
              {usage.by_extension.slice(0, 3).map(
                (b) => `${b.ext} ×${b.count}`,
              ).join(" · ")}
            </span>
          </div>
        )}
      </div>
      {hasOrphans && (
        <div
          className="mt-2 text-amber-300/90 text-xs"
          data-testid="downloads-storage-orphan-warning"
        >
          ⚠ {formatBytes(orphanBytes)} of older uploads have no durable
          Mongo storage and will be lost on the next redeploy. Re-upload
          those files once to make them persistent.
        </div>
      )}
    </div>
  );
};

export default function Downloads() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [storageUsage, setStorageUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [uploadDialog, setUploadDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [target, setTarget] = useState(null);

  // promote-to-release state (National officers only)
  const [promoteDialog, setPromoteDialog] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promoteVersion, setPromoteVersion] = useState("");
  const [promoteNotes, setPromoteNotes] = useState("");
  const [promoteProfile, setPromoteProfile] = useState("production");
  const [promoting, setPromoting] = useState(false);

  // upload state
  const [pickedFile, setPickedFile] = useState(null);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");

  // Read the logged-in user's chapter/title (the Hub stores them as
  // separate localStorage keys, NOT as a JSON `user` blob).
  const userChapter = localStorage.getItem("chapter") || "";
  const userTitle = localStorage.getItem("title") || "";
  const titleLists = useOfficerTitles();
  const isNationalOfficer =
    userChapter === "National" && titleLists.leadership_core_titles.includes(userTitle);

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/downloads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFiles(res.data?.files || []);
      const manage = !!res.data?.can_manage;
      setCanManage(manage);
      // Only admins get storage usage — endpoint 403s for everyone else.
      if (manage) {
        try {
          const u = await axios.get(`${API}/api/downloads/storage-usage`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setStorageUsage(u.data || null);
        } catch {
          setStorageUsage(null);
        }
      } else {
        setStorageUsage(null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load downloads");
    } finally {
      setLoading(false);
    }
  };

  const resetUploadForm = () => {
    setPickedFile(null);
    setDescription("");
    setProgress(0);
    setProgressLabel("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!pickedFile) {
      toast.error("Pick a file first");
      return;
    }
    if (pickedFile.size > MAX_FILE_SIZE) {
      toast.error(`File exceeds 2 GB max size`);
      return;
    }

    setUploading(true);
    setProgress(0);
    setProgressLabel("Initialising upload…");

    try {
      // 1. init session
      const initRes = await axios.post(
        `${API}/api/downloads/init`,
        {
          filename: pickedFile.name,
          total_size: pickedFile.size,
          description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const uploadId = initRes.data.upload_id;
      const chunkSize = initRes.data.max_chunk_size || CHUNK_SIZE;

      // 2. send chunks
      const totalChunks = Math.max(1, Math.ceil(pickedFile.size / chunkSize));
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, pickedFile.size);
        const blob = pickedFile.slice(start, end);

        const formData = new FormData();
        formData.append("chunk_index", i);
        formData.append("total_chunks", totalChunks);
        formData.append("chunk", blob, `part_${i}`);

        await axios.post(`${API}/api/downloads/chunk/${uploadId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        const pct = Math.round(((i + 1) / totalChunks) * 95);
        setProgress(pct);
        setProgressLabel(`Uploading… ${i + 1}/${totalChunks} chunks (${pct}%)`);
      }

      // 3. complete
      setProgressLabel("Finalising…");
      await axios.post(
        `${API}/api/downloads/complete/${uploadId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProgress(100);
      setProgressLabel("Done");
      toast.success("File uploaded");
      setUploadDialog(false);
      resetUploadForm();
      fetchDownloads();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (file) => {
    // Virtual rows (mobile APK releases) point to an external CDN — opening
    // them in a new tab lets the browser handle the actual download with
    // proper filename + CORS, avoiding the blob-fetch path entirely.
    if (file.external_url) {
      window.open(file.external_url, "_blank", "noopener,noreferrer");
      return;
    }
    // APK files (with or without "release" promotion) — route through the
    // public no-auth endpoint so mobile Chrome can hand the .apk straight to
    // Android's Package Installer. Blob downloads end up in the share sheet
    // on Android, which can't install APKs.
    if ((file.filename || "").toLowerCase().endsWith(".apk")) {
      window.open(`${API}/api/public/apk/${file.id}`, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      toast.info(`Downloading ${file.filename}…`);
      const res = await axios.get(`${API}/api/downloads/${file.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Download failed");
    }
  };

  const handlePromote = async () => {
    if (!promoteTarget || !promoteVersion.trim()) {
      toast.error("Version is required (e.g. 0.1.5)");
      return;
    }
    setPromoting(true);
    try {
      await axios.post(
        `${API}/api/mobile/releases/promote/${promoteTarget.id}`,
        {
          version: promoteVersion.trim(),
          notes: promoteNotes.trim() || undefined,
          profile: promoteProfile.trim() || "production",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Promoted to release v${promoteVersion.trim()} — members will be notified.`);
      setPromoteDialog(false);
      setPromoteTarget(null);
      setPromoteVersion("");
      setPromoteNotes("");
      setPromoteProfile("production");
      fetchDownloads();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Promote failed");
    } finally {
      setPromoting(false);
    }
  };

  const handleDelete = async () => {
    if (!target) return;
    try {
      await axios.delete(`${API}/api/downloads/${target.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("File deleted");
      setDeleteDialog(false);
      setTarget(null);
      fetchDownloads();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = files.filter((f) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      f.filename?.toLowerCase().includes(q) ||
      f.description?.toLowerCase().includes(q) ||
      f.uploaded_by_full_name?.toLowerCase().includes(q) ||
      f.uploaded_by?.toLowerCase().includes(q)
    );
  });

  // ID of the most recent APK release (for the "LATEST" pill on the Downloads card).
  const latestApkId = (() => {
    const apks = files.filter((f) => f.is_app_release);
    if (apks.length === 0) return null;
    return apks.reduce((best, cur) => {
      const a = new Date(best?.created_at || 0).getTime();
      const b = new Date(cur?.created_at || 0).getTime();
      return b > a ? cur : best;
    }, apks[0])?.id;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="downloads-back-btn"
              className="text-slate-300 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <HardDrive className="w-7 h-7 text-cyan-400" />
                Downloads
              </h1>
              <p className="text-sm text-slate-400">
                {canManage
                  ? "Upload files for the entire chapter ecosystem to download."
                  : "Files made available by National officers."}
              </p>
            </div>
          </div>
          {canManage && (
            <Button
              onClick={() => setUploadDialog(true)}
              data-testid="downloads-upload-open-btn"
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search files, descriptions, uploaders…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="downloads-search-input"
            className="pl-9 bg-slate-900 border-slate-700"
          />
        </div>

        {/* Admin-only storage indicator. Durable (Mongo) vs Declared bytes
            diverge when older rows lost their chunks pre-MongoDB-persistence
            fix; that's the signal for the user to re-upload them. */}
        {canManage && storageUsage && (
          <StorageUsageBar usage={storageUsage} />
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-slate-400" data-testid="downloads-loading">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="py-16 text-center text-slate-400" data-testid="downloads-empty">
              <HardDrive className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-lg">No files yet</p>
              <p className="text-sm">
                {canManage
                  ? "Upload the first file to share with your members."
                  : "Check back later — National officers will post files here."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="downloads-list">
            {filtered.map((f) => {
              const isLatestApk = f.is_app_release && f.id === latestApkId;
              return (
              <Card
                key={f.id}
                className={`bg-slate-900/60 transition-colors ${
                  isLatestApk
                    ? "border-cyan-500 ring-1 ring-cyan-500/50"
                    : "border-slate-800 hover:border-cyan-700"
                }`}
                data-testid={`downloads-card-${f.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    {getFileIcon(f.filename)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-base truncate" title={f.filename}>
                          {f.filename}
                        </CardTitle>
                        {isLatestApk && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-cyan-400 text-slate-950 text-[10px] font-extrabold tracking-wide"
                            data-testid={`downloads-latest-pill-${f.id}`}
                          >
                            <Sparkles className="w-2.5 h-2.5" />
                            LATEST
                          </span>
                        )}
                      </div>
                      <CardDescription className="text-xs mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded border border-slate-700 text-slate-300 text-xs">
                          {formatBytes(f.file_size)}
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  {f.description && (
                    <p className="text-sm text-slate-300 line-clamp-3">{f.description}</p>
                  )}
                  <div className="text-xs text-slate-400 space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="w-3 h-3" />
                      {f.uploaded_by_full_name || f.uploaded_by || "Unknown"}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      {formatDate(f.created_at)}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => handleDownload(f)}
                      data-testid={`downloads-download-btn-${f.id}`}
                      className="flex-1 bg-cyan-600 hover:bg-cyan-700 min-w-[120px]"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                    {isNationalOfficer &&
                      f.filename?.toLowerCase().endsWith(".apk") &&
                      !f.is_app_release && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPromoteTarget(f);
                            setPromoteVersion("");
                            setPromoteNotes("");
                            setPromoteProfile("production");
                            setPromoteDialog(true);
                          }}
                          data-testid={`downloads-promote-btn-${f.id}`}
                          className="border-cyan-500 text-cyan-300 hover:bg-cyan-900/40"
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          Promote
                        </Button>
                      )}
                    {canManage && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          setTarget(f);
                          setDeleteDialog(true);
                        }}
                        data-testid={`downloads-delete-btn-${f.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog
        open={uploadDialog}
        onOpenChange={(open) => {
          if (!uploading) {
            setUploadDialog(open);
            if (!open) resetUploadForm();
          }
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>File (max 2 GB)</Label>
              <Input
                type="file"
                ref={fileInputRef}
                onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
                disabled={uploading}
                data-testid="downloads-file-input"
                className="bg-slate-950 border-slate-700 text-white"
              />
              {pickedFile && (
                <p className="text-xs text-slate-400 mt-1">
                  {pickedFile.name} — {formatBytes(pickedFile.size)}
                </p>
              )}
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploading}
                placeholder="Briefly describe the file…"
                data-testid="downloads-description-input"
                className="bg-slate-950 border-slate-700 text-white"
                rows={3}
              />
            </div>
            {uploading && (
              <div className="space-y-2">
                <Progress value={progress} data-testid="downloads-upload-progress" />
                <p className="text-xs text-slate-400">{progressLabel}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setUploadDialog(false);
                resetUploadForm();
              }}
              disabled={uploading}
              data-testid="downloads-upload-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !pickedFile}
              data-testid="downloads-upload-submit-btn"
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote to release dialog */}
      <Dialog
        open={promoteDialog}
        onOpenChange={(open) => {
          if (!promoting) setPromoteDialog(open);
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              Promote APK to a release
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400">
            This adds <span className="font-semibold text-slate-200">{promoteTarget?.filename}</span>{" "}
            to the in-app "Update available" prompt + fires a push notification to all members.
          </p>
          <div className="space-y-3">
            <div>
              <Label>Version (e.g. 0.1.5)</Label>
              <Input
                value={promoteVersion}
                onChange={(e) => setPromoteVersion(e.target.value)}
                placeholder="0.1.5"
                disabled={promoting}
                data-testid="downloads-promote-version-input"
                className="bg-slate-950 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label>Release notes (optional)</Label>
              <Input
                value={promoteNotes}
                onChange={(e) => setPromoteNotes(e.target.value)}
                placeholder="Avatar upload, voice diagnose, build info"
                disabled={promoting}
                data-testid="downloads-promote-notes-input"
                className="bg-slate-950 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label>Profile</Label>
              <select
                value={promoteProfile}
                onChange={(e) => setPromoteProfile(e.target.value)}
                disabled={promoting}
                data-testid="downloads-promote-profile-select"
                className="w-full bg-slate-950 border border-slate-700 text-white rounded px-3 py-2 text-sm"
              >
                <option value="production">production</option>
                <option value="preview">preview</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPromoteDialog(false)}
              disabled={promoting}
              data-testid="downloads-promote-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePromote}
              disabled={promoting || !promoteVersion.trim()}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="downloads-promote-confirm-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {promoting ? "Promoting…" : "Promote & notify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Delete File?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300">
            Are you sure you want to delete <span className="font-semibold">{target?.filename}</span>?
            This cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteDialog(false)}
              data-testid="downloads-delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="downloads-delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
